import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";

import { useDepositQuote, useWallet } from "@/entities/wallet";
import { useCreateDeposit } from "@/features/create-deposit";
import { classify } from "@/shared/api";
import { ROUTES } from "@/shared/config";
import { formatMoney } from "@/shared/lib";
import { AmountInput, Button, useToast } from "@/shared/ui";
import { ScreenSkeleton } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Шаг 3: сумма и создание заявки. Каркас `Launch Amount` (22:157).
 *
 * Минимум показывается до отправки. Их два, и больший из них присылает
 * `GET /wallet/deposit-quote`: у платёжного шлюза свой порог на каждую монету,
 * и он бывает выше нашего `min_deposit`. Проверять минимум на клиенте всё
 * равно нельзя (сравнение сумм — это арифметика над деньгами), поэтому решает
 * бэкенд: ответ 400 с `details.min_deposit` превращается политикой ошибок в
 * `inline` и показывается у поля.
 *
 * Оценка «≈ N USDT» — тоже оттуда. Недоступность шлюза её не ломает: приходит
 * `estimated_pay_amount: null`, и строка просто говорит, что оценки нет. Экран
 * суммы не должен падать из-за витринного числа.
 */
export function DepositAmountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();

  const state = location.state as { asset?: string; network?: string; returnTo?: string } | null;
  const [amount, setAmount] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  const wallet = useWallet();
  const create = useCreateDeposit();
  // Хуки вызываются до любого раннего выхода: пустые строки дают выключенный
  // запрос, а не сбитый порядок хуков.
  const quote = useDepositQuote(state?.asset ?? "", state?.network ?? "", amount);

  if (!state?.asset || !state.network) return <Navigate to={ROUTES.depositAsset} replace />;

  if (wallet.isPending) return <ScreenSkeleton blocks={[96, 80, 50]} />;

  const { asset, network } = state;

  // Минимум шлюза перекрывает наш, когда он выше: показать меньший значило бы
  // позвать человека на сумму, которую платёж не примет.
  const minimum = quote.data?.min_amount ?? (wallet.isSuccess ? wallet.data.min_deposit : "0.00");

  const submit = () => {
    setFieldError(undefined);
    create.mutate(
      { amount, method: "crypto", asset, network },
      {
        onSuccess: (request) => {
          void navigate(ROUTES.deposit(request.id), { replace: true });
        },
        onError: (error) => {
          const reaction = classify(error);
          // Сумма ниже минимума — ошибка ввода, её место у поля, а не в тосте.
          if (reaction.kind === "inline") setFieldError(reaction.message);
          else if (reaction.kind === "conflict") toast.show({ tone: "loss", title: reaction.message });
          else if (reaction.kind !== "offline") toast.show({ tone: "loss", title: t("states.error.title") });
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("wallet.amountTitle")} subtitle={`${asset} · ${network}`} />

      <AmountInput
        value={amount}
        onChange={setAmount}
        error={fieldError}
        hint={t("wallet.minDeposit", {
          amount: formatMoney(minimum, { suffix: "USDT" }),
        })}
        suffix={asset}
      />

      {/* Сколько монет придётся отправить. Сумма приходит с бэкенда посчитанной:
          пересчёт «доллары → крипта» на клиенте — это арифметика над деньгами. */}
      {amount !== "" ? (
        <span className="px-1 text-caption font-normal text-text-secondary">
          {quote.data?.estimated_pay_amount
            ? t("wallet.quote.estimate", {
                amount: quote.data.estimated_pay_amount,
                currency: quote.data.pay_currency.toUpperCase(),
              })
            : t("wallet.quote.unavailable")}
        </span>
      ) : null}

      <Button size="cta" block disabled={amount === ""} loading={create.isPending} onClick={submit}>
        {t("wallet.getAddress")}
      </Button>
    </div>
  );
}
